import React from 'react';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../types/budget';
import { formatCurrency } from '../utils/budgetCalculations';
import { PencilIcon, TrashIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';

interface TransactionTableRowProps {
  transaction: Transaction;
  index: number;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  showActions?: boolean;
  onCreateInverse?: (transaction: Transaction) => void;
}

export const TransactionTableRow: React.FC<TransactionTableRowProps> = ({
  transaction,
  index,
  onEdit,
  onDelete,
  showActions = true,
  onCreateInverse,
}) => {
  const { t } = useTranslation();

  const handleCreateInverse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onCreateInverse) return;

    // Create a copy of the transaction with inverse amount
    const inverseTransaction: Transaction = {
      ...transaction,
      amount: -transaction.amount,
    };

    onCreateInverse(inverseTransaction);
  };

  const handleRowClick = () => {
    onEdit(transaction);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(transaction);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(transaction);
  };

  return (
    <tr
      className={`${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'} cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600`}
      onClick={handleRowClick}
    >
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-300 sm:px-6">
        {transaction.description}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-300 sm:px-6">
        {transaction.category}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-300 sm:px-6">
        {transaction.responsible === 'common'
          ? t('defaults.responsibles.common')
          : transaction.responsible}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-300 sm:px-6">
        {transaction.destination}
      </td>
      <td
        className={`whitespace-nowrap px-3 py-4 text-right text-sm font-medium sm:px-6 ${
          transaction.amount >= 0
            ? 'text-green-600 dark:text-green-400'
            : transaction.responsible === 'common'
              ? 'text-fuchsia-600 dark:text-rose-600'
              : 'text-red-600 dark:text-red-400'
        }`}
      >
        {formatCurrency(transaction.amount)}
      </td>
      {showActions && (
        <td
          className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-end space-x-2">
            <button
              onClick={handleEdit}
              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
              title={t('common.edit')}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            {onCreateInverse && (
              <button
                onClick={handleCreateInverse}
                className="text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-300"
                title={
                  transaction.amount < 0
                    ? t('transaction.createExpenseReport')
                    : t('transaction.createCashRefund')
                }
              >
                <ArrowsRightLeftIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
              title={t('common.delete')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};
